import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCriarServico, useAtualizarServico } from '@/hooks/useServicos';
import type { Servico, TipoPreco } from '@/types/servico';
import { getTipoPrecoLabels, getUnidadePadrao } from '@/types/servico';
import { Loader2, Package } from 'lucide-react';

interface ServicoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  servicoParaEditar?: Servico;
}

export function ServicoFormDialog({
  open,
  onOpenChange,
  servicoParaEditar,
}: ServicoFormDialogProps) {
  const { t } = useTranslation();
  const TIPO_PRECO_LABELS = getTipoPrecoLabels(t);
  const UNIDADE_PADRAO = getUnidadePadrao(t);
  const criarServico = useCriarServico();
  const atualizarServico = useAtualizarServico();

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipoPreco, setTipoPreco] = useState<TipoPreco>('fixo');
  const [valorBase, setValorBase] = useState('');
  const [unidade, setUnidade] = useState('');
  const [duracaoPadraoMinutos, setDuracaoPadraoMinutos] = useState('');

  useEffect(() => {
    if (servicoParaEditar) {
      setNome(servicoParaEditar.nome);
      setDescricao(servicoParaEditar.descricao || '');
      setTipoPreco(servicoParaEditar.tipo_preco as TipoPreco);
      setValorBase(servicoParaEditar.valor_base.toString());
      setUnidade(servicoParaEditar.unidade || '');
      setDuracaoPadraoMinutos(
        servicoParaEditar.duracao_padrao_minutos != null
          ? servicoParaEditar.duracao_padrao_minutos.toString()
          : ''
      );
    } else {
      resetForm();
    }
  }, [servicoParaEditar, open]);

  const resetForm = () => {
    setNome('');
    setDescricao('');
    setTipoPreco('fixo');
    setValorBase('');
    setUnidade('');
    setDuracaoPadraoMinutos('');
  };

  const handleTipoPrecoChange = (tipo: TipoPreco) => {
    setTipoPreco(tipo);
    setUnidade(UNIDADE_PADRAO[tipo]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const duracaoTrimmed = duracaoPadraoMinutos.trim();
    const dados = {
      nome: nome.trim(),
      descricao: descricao.trim(),
      tipo_preco: tipoPreco,
      valor_base: parseFloat(valorBase) || 0,
      unidade: unidade.trim() || UNIDADE_PADRAO[tipoPreco],
      duracao_padrao_minutos: duracaoTrimmed === '' ? null : parseInt(duracaoTrimmed, 10),
    };

    if (servicoParaEditar) {
      await atualizarServico.mutateAsync({
        id: servicoParaEditar.id,
        dados,
      });
    } else {
      await criarServico.mutateAsync(dados);
    }

    onOpenChange(false);
    resetForm();
  };

  const isPending = criarServico.isPending || atualizarServico.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {servicoParaEditar ? t('services.editService') : t('services.newService')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">{t('services.form.name')}</Label>
            <Input
              id="nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={t('services.form.namePlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="descricao">{t('services.form.description')}</Label>
            <Textarea
              id="descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder={t('services.form.descriptionPlaceholder')}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('services.form.priceType')}</Label>
            <Select value={tipoPreco} onValueChange={handleTipoPrecoChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TIPO_PRECO_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="valor">{t('services.form.baseValue')}</Label>
              <Input
                id="valor"
                type="number"
                step="0.01"
                min="0"
                value={valorBase}
                onChange={(e) => setValorBase(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unidade">{t('services.form.unit')}</Label>
              <Input
                id="unidade"
                value={unidade}
                onChange={(e) => setUnidade(e.target.value)}
                placeholder={UNIDADE_PADRAO[tipoPreco]}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duracao">{t('services.form.durationLabel')}</Label>
            <Input
              id="duracao"
              type="number"
              min={0}
              step={15}
              placeholder={t('services.form.durationPlaceholder')}
              value={duracaoPadraoMinutos}
              onChange={(e) => setDuracaoPadraoMinutos(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('services.form.durationHelp')}
            </p>
          </div>

          {servicoParaEditar && (
            <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              {t('services.priceChangeNote')}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !nome.trim()}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {servicoParaEditar ? t('common.save') : t('services.createService')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
